import React, { useState, useEffect } from "react";
import * as APIModels from "../../../../models";
import { ProcessServerService } from "../../../../services/ClientService";
import { Alert, Card, Checkbox, Stack } from "@mui/material";
import { CardContent } from "@material-ui/core";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

export default function ProcessServerFTPConfiguration({
    processServer,
}: {
    processServer: APIModels.ProcessServer;
}) {
    const [updatedProcessServer, setUpdatedProcessServer] =
        useState(processServer);
    const [ftpInfo, setFtpInfo] = useState<Partial<APIModels.FTPInfoRequest>>(
        processServer.ftpInfo || {}
    );
    const [editing, setEditing] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    useEffect(() => {
        setUpdatedProcessServer(processServer);
        setFtpInfo(processServer.ftpInfo || {});
    }, [processServer]);

    const handleEdit = () => {
        setEditing(true);
        setErrorMessage("");
        setSuccessMessage("");
    };

    const handleCancel = () => {
        setEditing(false);
        setFtpInfo(processServer.ftpInfo || {});
        setErrorMessage("");
        setSuccessMessage("");
    };

    const handleSave = async () => {
        setSaving(true);
        setErrorMessage("");
        setSuccessMessage("");

        try {
            // Update the process server with the new FTP info
            const updatedData = {
                ...updatedProcessServer,
                ftpInfo: ftpInfo,
            };

            const response = await ProcessServerService.update({
                id: processServer.id || "",
                body: updatedData,
            });

            if (response.successful && response.data) {
                setUpdatedProcessServer(response.data);
                setFtpInfo(response.data.ftpInfo || {});
                setEditing(false);
                setSuccessMessage("FTP configuration saved successfully!");
            } else {
                setErrorMessage(
                    response.message || "Failed to save FTP configuration"
                );
            }
        } catch (error) {
            setErrorMessage("An error occurred while saving FTP configuration");
            console.error("Error saving FTP configuration:", error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="card text-left">
            <div className="card-body">
                <h4 className="mb-3 bg-light p-2">FTP Configuration</h4>

                {successMessage && (
                    <Alert
                        icon={<CheckCircleIcon fontSize="inherit" />}
                        severity="success"
                        className="mb-3"
                        onClose={() => setSuccessMessage("")}
                    >
                        {successMessage}
                    </Alert>
                )}

                {errorMessage && (
                    <Alert
                        icon={<ErrorIcon fontSize="inherit" />}
                        severity="error"
                        className="mb-3"
                        onClose={() => setErrorMessage("")}
                    >
                        {errorMessage}
                    </Alert>
                )}

                <div className="row mb-3">
                    <div className="col-md-6">
                        <label htmlFor="ftpUrl" className="form-label">
                            FTP URL/Host
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            id="ftpUrl"
                            value={ftpInfo.url || ""}
                            onChange={(e) =>
                                setFtpInfo({ ...ftpInfo, url: e.target.value })
                            }
                            disabled={!editing}
                            placeholder="ftp.example.com"
                        />
                    </div>
                    <div className="col-md-6">
                        <label htmlFor="ftpPort" className="form-label">
                            Port
                        </label>
                        <input
                            type="number"
                            className="form-control"
                            id="ftpPort"
                            value={ftpInfo.port || 21}
                            onChange={(e) =>
                                setFtpInfo({
                                    ...ftpInfo,
                                    port: parseInt(e.target.value) || 21,
                                })
                            }
                            disabled={!editing}
                            placeholder="21"
                        />
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col-md-6">
                        <label htmlFor="ftpUsername" className="form-label">
                            Username
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            id="ftpUsername"
                            value={ftpInfo.username || ""}
                            onChange={(e) =>
                                setFtpInfo({
                                    ...ftpInfo,
                                    username: e.target.value,
                                })
                            }
                            disabled={!editing}
                            placeholder="FTP Username"
                        />
                    </div>
                    <div className="col-md-6">
                        <label htmlFor="ftpPassword" className="form-label">
                            Password
                        </label>
                        <div className="input-group">
                            <input
                                type={showPassword ? "text" : "password"}
                                className="form-control"
                                id="ftpPassword"
                                value={ftpInfo.password || ""}
                                onChange={(e) =>
                                    setFtpInfo({
                                        ...ftpInfo,
                                        password: e.target.value,
                                    })
                                }
                                disabled={!editing}
                                placeholder="FTP Password"
                            />
                            <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                <i
                                    className={`mdi ${
                                        showPassword ? "mdi-eye-off" : "mdi-eye"
                                    }`}
                                ></i>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col-md-6">
                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="appendClientMatter"
                                checked={ftpInfo.appendClientMatter || false}
                                onChange={(e) =>
                                    setFtpInfo({
                                        ...ftpInfo,
                                        appendClientMatter: e.target.checked,
                                    })
                                }
                                disabled={!editing}
                            />
                            <label
                                className="form-check-label"
                                htmlFor="appendClientMatter"
                            >
                                Append Client Matter to Path
                            </label>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="ftpActive"
                                checked={ftpInfo.active || false}
                                onChange={(e) =>
                                    setFtpInfo({
                                        ...ftpInfo,
                                        active: e.target.checked,
                                    })
                                }
                                disabled={!editing}
                            />
                            <label
                                className="form-check-label"
                                htmlFor="ftpActive"
                            >
                                FTP Active
                            </label>
                        </div>
                    </div>
                </div>

                <div className="text-end">
                    {!editing ? (
                        <button
                            className="btn btn-primary waves-effect waves-light mt-2"
                            onClick={handleEdit}
                        >
                            <i className="mdi mdi-pencil me-1"></i>
                            Edit FTP Configuration
                        </button>
                    ) : (
                        <>
                            {saving ? (
                                <button
                                    className="btn btn-success waves-effect waves-light mt-2 me-2"
                                    type="button"
                                    disabled={true}
                                >
                                    <span
                                        className="spinner-border spinner-border-sm me-1"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                    Saving...
                                </button>
                            ) : (
                                <>
                                    <button
                                        className="btn btn-secondary waves-effect waves-light mt-2 me-2"
                                        onClick={handleCancel}
                                    >
                                        <i className="mdi mdi-cancel me-1"></i>
                                        Cancel
                                    </button>
                                    <button
                                        className="btn btn-success waves-effect waves-light mt-2"
                                        onClick={handleSave}
                                    >
                                        <i className="mdi mdi-content-save me-1"></i>
                                        Save
                                    </button>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
